import { getAuth } from "firebase/auth";
import { USERS_API_BASE_URL } from "../config/api";

const BASE_URL = USERS_API_BASE_URL;

// App-wide "project" = a named collection of saved areas, referenced by id.
// Product-agnostic: Realtime monitors the members, Activities/ODM can aggregate
// them, etc.
//
// PERSISTENCE: the backend (Firestore via /user-data/projects) is the ONLY store.
// No localStorage — a local copy could diverge from the backend (some projects
// saved locally, some server-side), so every operation hits the backend and
// surfaces errors rather than silently falling back. See
// docs/projects-persistence.md for the endpoint contract.
export interface Project {
  id: string;
  name: string;
  description?: string;
  areaIds: string[];
  createdAt?: string; // ISO 8601
  updatedAt?: string; // ISO 8601
}

// Where a newly-saved area should be linked: an existing project, or a new one
// to create. Used by save flows (e.g. SavePolygonModal, SaveGeometries).
export type ProjectTarget =
  | { kind: "existing"; id: string }
  | { kind: "new"; name: string };

async function authHeaders(): Promise<Record<string, string>> {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not authenticated - please log in");
  const token = await user.getIdToken();
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

interface ApiError extends Error {
  status?: number;
}

// Backend call. Errors surface (no silent fallback) — projects are backend-only.
async function api(path: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`${BASE_URL}/user-data${path}`, {
    ...init,
    headers: await authHeaders(),
    mode: "cors",
  });
  if (!res.ok) {
    const err: ApiError = new Error(
      `Projects API ${res.status}: ${(await res.text()) || res.statusText}`
    );
    err.status = res.status;
    throw err;
  }
  return res.status === 204 ? null : res.json();
}

export const projectsApi = {
  async list(): Promise<Project[]> {
    const { projects } = await api("/projects");
    return projects || [];
  },

  async get(id: string): Promise<Project | null> {
    try {
      return await api(`/projects/${encodeURIComponent(id)}`);
    } catch (err) {
      if ((err as ApiError).status === 404) return null;
      throw err;
    }
  },

  async create(input: { name: string; description?: string; areaIds: string[] }): Promise<Project> {
    return api("/projects", { method: "POST", body: JSON.stringify(input) });
  },

  // Per-dashboard config (Trello #527): a free-form object stored on the project.
  // `config: null` = never saved; `{}` = saved-but-empty. PATCH merges top-level
  // keys and replaces arrays wholesale, so always send the full `groups` array.
  // `available: false` = the deployment has no such route yet (404) — callers
  // keep their device-local behaviour until it appears.
  async getConfig(
    id: string
  ): Promise<{ available: boolean; config: Record<string, unknown> | null }> {
    try {
      const { config } = await api(`/projects/${encodeURIComponent(id)}/config`);
      return { available: true, config: config ?? null };
    } catch (err) {
      if ((err as ApiError).status === 404) return { available: false, config: null };
      throw err;
    }
  },

  async patchConfig(id: string, patch: Record<string, unknown>): Promise<void> {
    await api(`/projects/${encodeURIComponent(id)}/config`, { method: "PATCH", body: JSON.stringify(patch) });
  },

  async update(
    id: string,
    patch: { name?: string; description?: string; areaIds?: string[] }
  ): Promise<void> {
    await api(`/projects/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  async rename(id: string, name: string): Promise<void> {
    return this.update(id, { name });
  },

  async addAreas(id: string, areaIds: string[]): Promise<void> {
    await api(`/projects/${encodeURIComponent(id)}/areas`, {
      method: "POST",
      body: JSON.stringify({ areaIds }),
    });
  },

  async remove(id: string): Promise<void> {
    await api(`/projects/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // --- user preferences (the "resume last dashboard" pointer, backend-stored) ---
  async getPreferences(): Promise<{ lastDashboardId?: string }> {
    try {
      return (await api("/preferences")) || {};
    } catch {
      return {}; // non-critical UX pointer; degrade quietly
    }
  },

  recordOpened(id: string): void {
    // Fire-and-forget persist of the resume pointer.
    api("/preferences", { method: "PATCH", body: JSON.stringify({ lastDashboardId: id }) }).catch(
      () => {}
    );
  },
};
